import { EstablishmentAPIPromiseClient } from "../../../gen/witbooking/backoffice/property/v1/establishment_api_grpc_web_pb";
import {
  GetPropertyRequest,
  Location,
  Property,
  PropertyTranslation,
  UpdatePropertyRequest,
} from "../../../gen/witbooking/backoffice/property/v1/establishment_api_pb";
import { buildGrpcClient } from "../../../lib/grpc";
import { DEFAULT_TIME_ZONE, PropertyEntity } from "../domain/property.entity";
import { Multimedia } from "../../../gen/witbooking/backoffice/common/common_pb";
import { Image } from "../../shared/widgets/ImageManager";

const grpcClient: EstablishmentAPIPromiseClient = buildGrpcClient(EstablishmentAPIPromiseClient);

function translationDtoFromEntity(obj: PropertyTranslation.AsObject): PropertyTranslation {
  const result = new PropertyTranslation();
  result.setLocale(obj.locale);
  result.setName(obj.name);
  result.setDescription(obj.description);
  return result;
}

function multimediaDtoFromEntity(obj: Multimedia.AsObject | undefined): Multimedia {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new Multimedia();
  result.setId(obj.id);
  result.setFilename(obj.filename);
  result.setUrl(obj.url);
  return result;
}
function imageFromMultimedia(multimedia: Multimedia.AsObject): Image | undefined {
  if (multimedia.filename === "empty.png") {
    return undefined;
  }
  return Object.assign(new File([""], multimedia.filename), {
    id: multimedia.id,
    url: multimedia.url,
    filename: multimedia.filename,
  });
}

function locationDtoFromEntity(obj: PropertyEntity): Location | undefined {
  if (!obj) {
    return undefined;
  }
  const result = new Location();
  result.setCountry(obj.country);
  result.setCity(obj.city);
  result.setAddress(obj.address);
  result.setZipCode(obj.zipCode);
  result.setLatitude(obj.latitude);
  result.setLongitude(obj.longitude);
  result.setTimeZone(obj.timeZone);
  return result;
}
function dtoFromEntity(obj: PropertyEntity): Property {
  const result = new Property();
  result.setId(obj.id);
  result.setTicker(obj.ticker);
  result.setPropertyType(obj.propertyType);
  result.setValuation(obj.valuation);
  result.setLocation(locationDtoFromEntity(obj));
  result.setPhone(obj.phone);
  result.setAdminEmail(obj.adminEmail);
  result.setContactEmail(obj.contactEmail);
  result.setTranslationsList(obj.translationsList.map(translationDtoFromEntity));
  result.setMultimediaList(obj.multimediaList.map(multimediaDtoFromEntity));
  return result;
}

export async function getEntity(token: string): Promise<PropertyEntity> {
  const request = new GetPropertyRequest();
  const response = await grpcClient.getProperty(request, {
    Authorization: `Bearer ${token}`,
  });
  const entity = response.getProperty();
  if (!entity) {
    throw new Error("Unexpected reply obtaining property");
  }
  const { location, multimediaList = [], ...obj } = entity.toObject();
  if (!location) {
    throw new Error("Property location cannot be undefined");
  }
  const images = multimediaList.map(imageFromMultimedia).filter((it): it is Image => Boolean(it));

  return {
    ...obj,
    ...location,
    timeZone: location.timeZone || DEFAULT_TIME_ZONE,
    multimediaList: images,
  };
}

export async function updateEntity(entity: PropertyEntity, token: string): Promise<void> {
  const oldEntity = await getEntity(token);
  const newEntity: PropertyEntity = {
    ...entity,
    multimediaList: oldEntity.multimediaList,
  };
  const request = new UpdatePropertyRequest();
  request.setProperty(dtoFromEntity(newEntity));
  await grpcClient.updateProperty(request, {
    Authorization: `Bearer ${token}`,
  });
}
